import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, PhoneCall } from 'lucide-react';
import Button from '../UI/Button';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { settings } = useSystemSettings();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${scrolled || isMenuOpen
        ? 'bg-primary shadow-md py-2'
        : 'bg-primary py-4'
        }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center z-20">
            <div className="flex-shrink-0">
              <img
                src="/logo-white.svg"
                alt="Protecția Animalelor S1"
                className="h-10 w-auto"
              />
            </div>
            <div className="ml-3">
              <span className="text-white font-display font-bold text-base sm:text-lg leading-none block">
                Protecția
              </span>
              <span className="text-white/90 font-display text-xs sm:text-sm leading-none block mt-0.5">
                Animalelor S1
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-1">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-4 py-2 font-medium rounded-md transition-colors ${isActive
                  ? 'text-white font-semibold'
                  : 'text-white/80 hover:text-white'
                }`
              }
            >
              Acasă
            </NavLink>
            <NavLink
              to="/caini"
              className={({ isActive }) =>
                `px-4 py-2 font-medium rounded-md transition-colors ${isActive
                  ? 'text-white font-semibold'
                  : 'text-white/80 hover:text-white'
                }`
              }
            >
              Câinii noștri
            </NavLink>
            <NavLink
              to="/proiect-miau-casa"
              className={({ isActive }) =>
                `px-4 py-2 font-medium rounded-md transition-colors ${isActive
                  ? 'text-white font-semibold'
                  : 'text-white/80 hover:text-white'
                }`
              }
            >
              Proiect Miau Casa
            </NavLink>
            <NavLink
              to="/despre-noi"
              className={({ isActive }) =>
                `px-4 py-2 font-medium rounded-md transition-colors ${isActive
                  ? 'text-white font-semibold'
                  : 'text-white/80 hover:text-white'
                }`
              }
            >
              Despre noi
            </NavLink>
            {/* <NavLink
              to="/doneaza"
              className={({ isActive }) =>
                `px-4 py-2 font-medium rounded-md transition-colors ${
                  isActive
                    ? 'text-white font-semibold'
                    : 'text-white/80 hover:text-white'
                }`
              }
            >
              Donează
            </NavLink> */}
            <NavLink
              to="/intrebari"
              className={({ isActive }) =>
                `px-4 py-2 font-medium rounded-md transition-colors ${isActive
                  ? 'text-white font-semibold'
                  : 'text-white/80 hover:text-white'
                }`
              }
            >
              Întrebări frecvente
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `px-4 py-2 font-medium rounded-md transition-colors ${isActive
                  ? 'text-white font-semibold'
                  : 'text-white/80 hover:text-white'
                }`
              }
            >
              Contact
            </NavLink>
          </nav>

          {/* Phone & Adopt Button */}
          <div className="hidden xl:flex items-center space-x-4">
            <a
              href={`tel:${settings?.phone || '+40750209751'}`}
              className="flex items-center text-white/80 hover:text-white transition-colors"
            >
              <PhoneCall size={18} className="mr-2" />
              <span className="font-medium">{settings?.phone || '+40 750 209 751'}</span>
            </a>
            <Button href="/caini" variant="white">
              Adoptă
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden text-white hover:text-white/80 focus:outline-none z-20"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Mobile Menu Overlay */}
          {isMenuOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-10"
              onClick={() => setIsMenuOpen(false)}
            />
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`xl:hidden fixed top-0 right-0 w-[80%] h-full bg-primary shadow-lg transition-transform duration-300 ease-in-out transform z-50 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="pt-20 px-4 pb-6 h-full overflow-y-auto">
          <nav className="flex flex-col space-y-2">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-4 py-3 font-medium rounded-md transition-colors ${isActive
                  ? 'bg-primary-dark text-white font-semibold'
                  : 'text-white/80 hover:text-white hover:bg-primary-dark'
                }`
              }
            >
              Acasă
            </NavLink>
            <NavLink
              to="/caini"
              className={({ isActive }) =>
                `px-4 py-3 font-medium rounded-md transition-colors ${isActive
                  ? 'bg-primary-dark text-white font-semibold'
                  : 'text-white/80 hover:text-white hover:bg-primary-dark'
                }`
              }
            >
              Câinii noștri
            </NavLink>
            <NavLink
              to="/proiect-miau-casa"
              className={({ isActive }) =>
                `px-4 py-3 font-medium rounded-md transition-colors ${isActive
                  ? 'bg-primary-dark text-white font-semibold'
                  : 'text-white/80 hover:text-white hover:bg-primary-dark'
                }`
              }
            >
              Proiect Miau Casa
            </NavLink>
            <NavLink
              to="/despre-noi"
              className={({ isActive }) =>
                `px-4 py-3 font-medium rounded-md transition-colors ${isActive
                  ? 'bg-primary-dark text-white font-semibold'
                  : 'text-white/80 hover:text-white hover:bg-primary-dark'
                }`
              }
            >
              Despre noi
            </NavLink>
            {/* <NavLink
              to="/doneaza"
              className={({ isActive }) =>
                `px-4 py-3 font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary-dark text-white font-semibold'
                    : 'text-white/80 hover:text-white hover:bg-primary-dark'
                }`
              }
            >
              Donează
            </NavLink> */}
            <NavLink
              to="/intrebari"
              className={({ isActive }) =>
                `px-4 py-3 font-medium rounded-md transition-colors ${isActive
                  ? 'bg-primary-dark text-white font-semibold'
                  : 'text-white/80 hover:text-white hover:bg-primary-dark'
                }`
              }
            >
              Întrebări frecvente
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `px-4 py-3 font-medium rounded-md transition-colors ${isActive
                  ? 'bg-primary-dark text-white font-semibold'
                  : 'text-white/80 hover:text-white hover:bg-primary-dark'
                }`
              }
            >
              Contact
            </NavLink>

            <div className="pt-4 mt-2 border-t border-primary-dark">
              <a
                href={`tel:${settings?.phone || '+40750209751'}`}
                className="flex items-center px-4 py-3 text-white/80 hover:text-white rounded-md"
              >
                <PhoneCall size={18} className="mr-2" />
                <span>{settings?.phone || '+40 750 209 751'}</span>
              </a>
            </div>

            <div className="pt-2">
              <Button href="/caini" variant="white" className="w-full">
                Adoptă un prieten
              </Button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;